import type {
  AgentFile,
  AgentFileBatchOperation,
  AgentFileFindResponse,
  AgentFilePresignedReadResponse,
  AgentFileReadResponse,
  AgentFileRef,
  AgentFileResponse,
  AgentFileWriteResponse,
  AgentFileWriteUrlResponse,
} from "schemas/generated/projectFile";
import type { SandboxFile } from "schemas/generated/sandboxFiles";
import type { BedrockAgent } from "./agent/bedrock-agent";
import type { RequestFn } from "./request";
import type { SandboxFileEncoding } from "./sandbox-files";

/**
 * Two surfaces over the same Drive: `SandboxFs` (compute RPC, path-only, needs a
 * booted sandbox) and `DriveFs` (HTTP, no sandbox, the durable surface below).
 */
export type FsReadSingleInput = {
  path: string;
  encoding?: SandboxFileEncoding;
};
export type FsReadBatchInput = {
  paths: string[];
  encoding?: SandboxFileEncoding;
};
export type FsReadSingleResult = { content: string; sizeBytes: number };
export type SandboxFsReadSingleResult = FsReadSingleResult & {
  revision: string;
};
export type FsReadBatchResult = { files: Record<string, string | null> };
export type FsWriteInput = {
  path: string;
  content: string;
  encoding?: SandboxFileEncoding;
};
export type FsWriteAtomicInput = FsWriteInput &
  (
    | { expectedRevision: string; mustNotExist?: never }
    | { expectedRevision?: never; mustNotExist: true }
  );
export type FsMoveInput = {
  sourcePath: string;
  destinationPath: string;
};
export type FsFindInput = {
  patterns: string[];
  directory: string;
  ignore?: string[];
};

/** Address a Drive file by path or by stable id. */
export type DriveRef = { path: string } | { id: string };

/** Path-only filesystem (sandbox RPC); the compute-local working surface. */
export interface AgentFilesystem {
  /** Single read collapses a missing file to `""` (sandbox `readFile` semantics). */
  read(input: FsReadSingleInput): Promise<SandboxFsReadSingleResult>;
  /** Batch read returns `null` for a missing path, `""` for an existing-but-empty one. */
  read(input: FsReadBatchInput): Promise<FsReadBatchResult>;
  write(input: FsWriteInput): Promise<{ sizeBytes: number; revision: string }>;
  writeAtomic(
    input: FsWriteAtomicInput,
  ): Promise<{ sizeBytes: number; revision: string }>;
  move(input: FsMoveInput): Promise<{ ok: true }>;
  find(input: FsFindInput): Promise<{ files: SandboxFile[] }>;
  delete(input: { path: string }): Promise<{ ok: true }>;
}

/**
 * The durable Drive surface. Inline `read`/`write`/`find` are path-based; the
 * ref-addressed (path or id) verbs let id-keyed (draw) and path-keyed apps share
 * one interface. Transport is per-verb: inline vs. presigned direct-R2.
 */
export interface DriveFs {
  read(input: FsReadSingleInput): Promise<FsReadSingleResult>;
  read(input: FsReadBatchInput): Promise<FsReadBatchResult>;
  /** Ordered writes/deletes applied as one serialized unit per project — no interleaving, no rollback. */
  batch(input: {
    operations: AgentFileBatchOperation[];
  }): Promise<{ ok: true }>;
  write(input: FsWriteInput): Promise<{ sizeBytes: number }>;
  find(input: FsFindInput): Promise<{ files: SandboxFile[] }>;
  delete(input: DriveRef): Promise<{ ok: true }>;
  stat(input: DriveRef): Promise<AgentFile>;
  presignedRead(
    input: DriveRef,
  ): Promise<{ downloadUrl: string; sizeBytes: number | null }>;
  presignedWrite(
    input: DriveRef & { contentType: string; sizeBytes: number },
  ): Promise<{ uploadUrl: string }>;
  confirmWrite(
    input: DriveRef & { contentType: string; sizeBytes: number },
  ): Promise<AgentFile>;
}

const BATCH = "/api/v1/files/batch";
const READ = "/api/v1/files/read";
const WRITE = "/api/v1/files/write";
const FIND = "/api/v1/files/find";
const DELETE = "/api/v1/files/delete";
const STAT = "/api/v1/files/stat";
const PRESIGNED_READ = "/api/v1/files/read/presigned";
const PRESIGNED_WRITE = "/api/v1/files/write/presigned";
const CONFIRM_WRITE = "/api/v1/files/write/confirm";

function utf8ByteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

// Raw byte count a base64 string decodes to, without materializing the bytes.
function base64ByteLength(base64: string): number {
  const trimmed = base64.replace(/=+$/, "");
  return Math.floor((trimmed.length * 3) / 4);
}

/** Lower the ergonomic `{ path } | { id }` ref onto the discriminated wire ref. */
function toWireRef(ref: DriveRef): AgentFileRef {
  return "id" in ref
    ? { kind: "id", id: ref.id }
    : { kind: "path", path: ref.path };
}

/** Sandbox-backed filesystem: a thin adapter over the existing `BedrockAgent` file RPCs. */
export function createSandboxFs(agent: BedrockAgent): AgentFilesystem {
  function read(input: FsReadSingleInput): Promise<SandboxFsReadSingleResult>;
  function read(input: FsReadBatchInput): Promise<FsReadBatchResult>;
  function read(
    input: FsReadSingleInput | FsReadBatchInput,
  ): Promise<FsReadSingleResult | FsReadBatchResult> {
    if ("paths" in input) {
      return agent.readFiles(input);
    }
    return agent.readFile(input);
  }
  return {
    read,
    write: (input) => agent.writeFile(input),
    writeAtomic: (input) => agent.writeFileAtomic(input),
    move: (input) => agent.moveFile(input),
    find: (input) => agent.findFiles(input),
    delete: (input) => agent.deleteFile(input),
  };
}

/**
 * Drive-backed filesystem: the ref-addressed bedrock-api verb routes (Postgres
 * + R2, no sandbox). Inline read/write take an `encoding` (`base64` round-trips
 * raw bytes); large blobs go through the presigned verbs.
 */
export function createDriveFs({
  request,
  getProjectId,
}: {
  request: RequestFn;
  getProjectId: () => string;
}): DriveFs {
  function read(input: FsReadSingleInput): Promise<FsReadSingleResult>;
  function read(input: FsReadBatchInput): Promise<FsReadBatchResult>;
  async function read(
    input: FsReadSingleInput | FsReadBatchInput,
  ): Promise<FsReadSingleResult | FsReadBatchResult> {
    const projectId = getProjectId();
    if ("paths" in input) {
      return request<AgentFileReadResponse>({
        method: "POST",
        path: READ,
        body: { projectId, paths: input.paths, encoding: input.encoding },
      });
    }
    const result = await request<AgentFileReadResponse>({
      method: "POST",
      path: READ,
      body: { projectId, paths: [input.path], encoding: input.encoding },
    });
    // A missing file reads as empty, not an error (sandbox `readFile` contract).
    const content = result.files[input.path] ?? "";
    const sizeBytes =
      input.encoding === "base64"
        ? base64ByteLength(content)
        : utf8ByteLength(content);
    return { content, sizeBytes };
  }
  return {
    read,
    batch: (input) => {
      return request<{ ok: true }>({
        method: "POST",
        path: BATCH,
        body: { projectId: getProjectId(), operations: input.operations },
      });
    },
    write: (input) => {
      return request<AgentFileWriteResponse>({
        method: "POST",
        path: WRITE,
        body: {
          projectId: getProjectId(),
          path: input.path,
          content: input.content,
          encoding: input.encoding,
        },
      });
    },
    find: (input) => {
      return request<AgentFileFindResponse>({
        method: "POST",
        path: FIND,
        body: {
          projectId: getProjectId(),
          directory: input.directory,
          patterns: input.patterns,
          ignore: input.ignore,
        },
      });
    },
    delete: (input) => {
      return request<{ ok: true }>({
        method: "POST",
        path: DELETE,
        body: { projectId: getProjectId(), ref: toWireRef(input) },
      });
    },
    stat: (input) => {
      return request<AgentFileResponse>({
        method: "POST",
        path: STAT,
        body: { projectId: getProjectId(), ref: toWireRef(input) },
      }).then((response) => response.file);
    },
    presignedRead: (input) => {
      return request<AgentFilePresignedReadResponse>({
        method: "POST",
        path: PRESIGNED_READ,
        body: { projectId: getProjectId(), ref: toWireRef(input) },
      });
    },
    presignedWrite: (input) => {
      return request<AgentFileWriteUrlResponse>({
        method: "POST",
        path: PRESIGNED_WRITE,
        body: {
          projectId: getProjectId(),
          ref: toWireRef(input),
          contentType: input.contentType,
          sizeBytes: input.sizeBytes,
        },
      });
    },
    confirmWrite: (input) => {
      return request<AgentFileResponse>({
        method: "POST",
        path: CONFIRM_WRITE,
        body: {
          projectId: getProjectId(),
          ref: toWireRef(input),
          contentType: input.contentType,
          sizeBytes: input.sizeBytes,
        },
      }).then((response) => response.file);
    },
  };
}
